<script setup lang="ts">
    import AllstarsRegister from '@/components/AllstarsRegister.ce.vue';
    import PackageSelection from '@/components/packages/PackageSelection.ce.vue';
    import Tutorial from '@/components/quote/tutorial/Tutorial.ce.vue';
    import SubmitConfirmation from '@/components/ui/SubmitConfirmation.ce.vue';
    import { SURVEY_APPOINTMENT } from '@/constants/survey';
    import { getProductCategories, getProducts, getSpaces } from '@/helpers/axios';
    import { forceAncestorsToTop } from '@/helpers/scroll';
    import { type ProductCategoryTyping, type ProductTyping, type SmallCardTyping, type SurveyItem } from '@/typings/widget';
    import useEditableClone from '@shared/composables/useEditableClone';
    import { ProductPricingStrategy } from '@shared/constants/payments';
    import { DateTime } from '@shared/helpers';
    import { type IdTyping } from '@shared/typings';
    import sortBy from 'lodash/sortBy';
    import { type ComputedRef, type Ref } from 'vue';
    import LoadingCard from '../cards/LoadingCard.ce.vue';
    import Amount from './amount/Amount.ce.vue';
    import Cards from './cards/Cards.ce.vue';
    import DateSelection from './date/DateSelection.ce.vue';
    import EventSelection from './event/EventSelection.ce.vue';
    import Form from './form/Form.ce.vue';
    import Layout from './layout/Layout.ce.vue';
    import ProgramSelection from './program/ProgramSelection.ce.vue';
    import Shop from './shop/Shop.ce.vue';
    import SmallCards from './small-cards/SmallCards.ce.vue';
    import SpaceSelection from './space/SpaceSelection.ce.vue';
    import SurveySelection from './survey/SurveySelection.ce.vue';
    import TemplateSelection from './template/TemplateSelection.ce.vue';

    const props = defineProps<{
        venue: string
        locale: string
        inPath?: boolean
        maxGuestAmount?: number
        minGuestAmount?: number
        guestAmountStep?: number
    }>();

    const emit = defineEmits<{(event: 'backToPath' | 'backToMain' | 'scrollToTop' | 'callSelected')}>();

    const { t } = useI18n();

    // Stores
    const pathStore = useWidgetPathStore();
    const { TIMEFRAMES } = storeToRefs(pathStore);
    const {
        eventTypeId,
        guestTypeIds,
        timeframeId,
        selectedDate,
        surveyAppointment,
        templateId,
        productCategoryId,
        guestAmount,
        program,
        decorationId,
        entertainmentId,
        spaceIds,
        productIds,
        bookerName,
    } = storeToRefs(pathStore);
    const {
        venueEventTypes,
        venue: venueData,
        venueProductCategories,
        filteredVenueProductCategories,
        venueProducts,
        venueSpaces,
        isPackageActive,
        packageListFilter,
    } = storeToRefs(useWidgetVenueStore());
    const { resetPathStore } = pathStore;

    // Helpers
    const getFilteredProductsByCategory = (categoryId: IdTyping): ProductTyping[] => {
        // First, filter product by category id
        const categoryFilteredProducts = venueProducts.value.filter(product => product.categories.some(cat => cat.id === categoryId));

        // Then, filter products by selected event type
        const eventFilteredProducts = categoryFilteredProducts.filter(product => product.eventTypes.some(eventType => eventType.id === eventTypeId.value));

        // Finally, filter products by selected guest types
        return eventFilteredProducts.filter(product => {
            if (product.pricingStrategy === ProductPricingStrategy.PerGuesttype) {
                return product.guestTypes.some(guestType => guestTypeIds.value.includes(guestType.id));
            }
            return true;
        });
    };

    // Const
    const STEPS_IN_PATH = 10;
    const STEPS_OUT_PATH = 11;

    // Refs
    const scrollContainer: Ref<HTMLElement | null> = ref(null);
    const step = ref(0);
    const guestAmountChanged = ref(false);
    const registerView = ref(false);
    const selectPackages: Ref<boolean> = ref(false);
    const surveyAppointmentItems: Ref<SurveyItem[]> = ref([]);

    // Computed
    const totalSteps: ComputedRef<number> = computed(() => props.inPath ? STEPS_IN_PATH : STEPS_OUT_PATH);
    const guestTypes = computed(() => venueEventTypes.value.find(evenType => evenType.id === eventTypeId.value)?.guestTypes);
    const guestTypeItems: ComputedRef<SmallCardTyping[]> = computed(() => guestTypes.value?.map(guestType => ({
        id: guestType.id,
        title: guestType.name,
    })) ?? []);
    const timeframes: ComputedRef<SmallCardTyping[]> = computed(() => TIMEFRAMES.value.map(timeframe => ({
        id: timeframe.id,
        title: timeframe.name,
        image: timeframe.image,
        hoverImage: timeframe.hoverImage,
    })));
    const templates = computed(() => venueData.value?.templates
        .filter(template => template.eventTypes
            .map(eventType => eventType.id)
            .includes(eventTypeId.value))
        ?? []);
    const selectedGuestTypes = computed(() => guestTypes.value?.filter(guestType => guestTypeIds.value.includes(guestType.id)));
    const eventName: ComputedRef<string> = computed(() => venueEventTypes.value.find(event => Number(event.id) === Number(eventTypeId.value))?.name ?? '');
    const productsForEventType: ComputedRef<ProductTyping[]> = computed(() =>
        venueProducts.value.filter(product => product.eventTypes.some(eventType => eventType.id === eventTypeId.value)));
    const spacesForEventType: ComputedRef<typeof venueSpaces.value> = computed(() =>
        venueSpaces.value.filter(space => space.eventTypes.some(eventType => eventType.id === eventTypeId.value)));
    const categoriesWithProducts: ComputedRef<ProductCategoryTyping[]> = computed(() =>
        filteredVenueProductCategories.value.filter(category => getFilteredProductsByCategory(category.id).length > 0));
    const productCategoryItems: ComputedRef<SmallCardTyping[]> = computed(() => {
        const items = categoriesWithProducts.value.map(category => ({
            id: category.id,
            subtitle: category.name,
            image: category.profileImage?.fullImagePath,
        }));
        return [
            ...sortBy(items.filter(item => item.image), 'subtitle'),
            ...sortBy(items.filter(item => !item.image), 'subtitle'),
        ];
    });

    // Methods
    const handleSelectEventType = (nextStep: number): void => {
        const selectedEventType = venueEventTypes.value.find(event => Number(event.id) === eventTypeId.value);
        const isBusinessEvent = selectedEventType?.business;

        if (isBusinessEvent && isPackageActive.value) {
            selectPackages.value = true;
            step.value = 1;
            return;
        }

        if (isBusinessEvent && !isPackageActive.value) {
            emit('callSelected');
            return;
        }

        step.value = nextStep;
    };

    const scrollToTop = async(): Promise<void> => {
        // Wait for the next screen to render before resetting the scroll position.
        await nextTick();

        // Force the inner scroll container and every ancestor up the tree to the top.
        forceAncestorsToTop(scrollContainer.value);

        // Re-run after the browser has laid out the new screen (heights can change post-render),
        // so the reset is not left mid-page on mobile.
        requestAnimationFrame(() => { forceAncestorsToTop(scrollContainer.value) });

        // ...and bubble up so the host's main-content is reset too.
        emit('scrollToTop');
    };

    const handleToMain = (): void => {
        resetPathStore();
        emit('backToMain');
    };

    const handleGetProductCategories = async(): Promise<void> => {
        try {
            venueProductCategories.value = await getProductCategories(props.venue, props.locale);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Backend call failed', err);
        }
    };

    const handleGetProducts = async() :Promise<void> => {
        try {
            venueProducts.value = await getProducts(props.venue, props.locale);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Backend call failed', err);
        }
    };

    const handleGetSpaces = async(): Promise<void> => {
        try {
            venueSpaces.value = await getSpaces(props.venue, props.locale);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading spaces:', error);
        }
    };

    const populateSurveyItems = async(value: string): Promise<void> => {
        surveyAppointmentItems.value = [];
        await nextTick(() => {
            const date = DateTime.dayjsObject(value);

            const [
                weekday, day, month, year,
            ] = date.format('dddd D MMMM YYYY')
                .split(' ');
            surveyAppointmentItems.value = useEditableClone(SURVEY_APPOINTMENT);
            surveyAppointmentItems.value[0].label = `${t('widget.survey-appointment.date-only')} ${weekday} ${day} ${month} ${year}`;
            surveyAppointmentItems.value[1].label = `${t('widget.survey-appointment.date-some-1')} ${weekday} ${t('widget.survey-appointment.date-some-2')} ${month} ${year}`;
        });
    };

    const handleNextTutorialStep = (): void => {
        if (venueData.value?.allStarsQuoteEnabled) {
            registerView.value = true;
        } else {
            props.inPath ? step.value = 2 : step.value = 1;
        }
    };

    const handleNextDecorationStep = (): void => {
        const firstItems = getFilteredProductsByCategory(4);
        if (!firstItems.length) {
            const secondItems = getFilteredProductsByCategory(5);
            if (!secondItems.length) {
                step.value = 11;
                return;
            }
            step.value = 10;
            return;
        }
        step.value = 9;
    };

    const handleNextCategory = (): void => {
        const secondItems = getFilteredProductsByCategory(5);
        step.value = secondItems.length ? 10 : 11;
    };

    const handleBackFromSpaceSelection = (): void => {
        const entertainmentItems = getFilteredProductsByCategory(5);
        if (!entertainmentItems.length) {
            const decorationItems = getFilteredProductsByCategory(4);
            if (!decorationItems.length) {
                step.value = 8;
                return;
            }
            step.value = 9;
            return;
        }
        step.value = 10;
    };

    const handleBackFromEntertainmentSelection = (): void => {
        const decorationItems = getFilteredProductsByCategory(4);
        if (!decorationItems.length) {
            step.value = 8;
            return;
        }
        step.value = 9;
    };

    // Hooks
    onMounted(async() => {
        await Promise.allSettled([
            handleGetProductCategories(),
            handleGetProducts(),
            handleGetSpaces(),
        ]);
    });

    onBeforeUnmount(() => {
        step.value = 0;
        resetPathStore();
    });

    // Watchers
    watch(() => step.value, () => {
        emit('scrollToTop');
    });

    watch(() => registerView.value, nVal => {
        if (nVal) emit('scrollToTop');
    });

    watch(() => templateId.value, nVal => {
        if (nVal) program.value = templates.value.find(template => template.id === templateId.value)?.programme ?? [];
    });

    watch(() => guestAmount.value, nVal => {
        if (nVal) guestAmountChanged.value = true;
    });

    watch(() => step.value, nVal => {
        if (nVal === 7) guestAmountChanged.value = false;
    });

    watch(() => selectedDate.value, async nVal => {
        if (nVal) {
            await populateSurveyItems(nVal);
        }
    });

    watch(() => eventTypeId.value, nVal => {
        if (!nVal) return;
        packageListFilter.value.eventType = nVal;

        if (step.value !== 1) return;

        const selectedEventType = venueEventTypes.value.find(event => Number(event.id) === Number(nVal));
        if (!selectedEventType?.business) return;

        // Wait for OptionsCard's slide-up exit animation (0.75s CSS transition) to finish before mounting PackageSelection
        if (isPackageActive.value) {
            selectPackages.value = true;
            return;
        }
        emit('callSelected');
    });
</script>

<template>
    <div
        id="quote"
        class="quote"
    >
        <div
            v-if="selectPackages"
            ref="scrollContainer"
            class="path-view__scroll"
        >
            <PackageSelection
                :locale="locale"
                :venue="venue"
                in-path
                @scroll-to-top="scrollToTop"
                @back-to-main="handleToMain"
                @back-to-path="selectPackages = false"
            />
        </div>
        <AllstarsRegister
            v-else-if="registerView"
            :venue="venueData"
            :name="null"
            @back-to-main="emit('backToMain')"
            @back-to-path="registerView = false"
        />
        <template v-else>
            <Tutorial
                v-if="step === 0"
                :in-path="inPath"
                @back-to-path="emit('backToPath')"
                @next="handleNextTutorialStep"
            />

            <!-- Event Type Selection -->
            <Layout
                v-if="step === 1 && !inPath"
                :back="t('widget.quote.event-selection.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 0"
            >
                <EventSelection
                    v-model="eventTypeId"
                    :items="venueEventTypes"
                    @next="handleSelectEventType(2)"
                />
            </Layout>

            <!-- Guest Type Selection -->
            <Layout
                v-if="step === 2"
                :back="inPath ? t('widget.quote.event-selection.back') : t('widget.quote.guest-selection.back')"
                :step="step"
                :total="totalSteps"
                @back="inPath ? step = 0 : step = 1"
            >
                <SmallCards
                    v-model="guestTypeIds"
                    :items="guestTypeItems"
                    :title="t('widget.quote.guest-selection.title')"
                    multiple
                    @next="step = 3"
                />
            </Layout>

            <!-- Timeframe Selection -->
            <Layout
                v-if="step === 3"
                :back="t('widget.quote.timeframes.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 2"
            >
                <SmallCards
                    v-model="timeframeId"
                    :items="timeframes"
                    :title="t('widget.quote.timeframes.title')"
                    @next="step = 4"
                />
            </Layout>

            <!-- Date Selection -->
            <Layout
                v-if="step === 4"
                :back="t('widget.quote.date.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 3"
            >
                <DateSelection
                    v-model="selectedDate"
                    @next="step = 5"
                />
            </Layout>

            <!-- Survey Selection -->
            <Layout
                v-if="step === 5"
                :back="t('widget.quote.survey.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 4"
            >
                <SurveySelection
                    v-model="surveyAppointment"
                    :items="surveyAppointmentItems"
                    :date="selectedDate"
                    @next="step = 6"
                />
            </Layout>

            <!-- Template Selection -->
            <Layout
                v-if="step === 6"
                :back="t('widget.quote.template.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 5"
            >
                <TemplateSelection
                    v-model="templateId"
                    :templates="templates"
                    @next="step = 7"
                />
            </Layout>

            <!-- Guest Amount Selection -->
            <Layout
                v-if="step === 7"
                :back="t('widget.quote.amount.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 6"
            >
                <Amount
                    v-model="guestAmount"
                    :max-guest-amount="maxGuestAmount"
                    :min-guest-amount="minGuestAmount"
                    :guest-amount-step="guestAmountStep"
                    :event="eventName"
                    @next="step = 8"
                />
            </Layout>

            <!-- Program Selection -->
            <Layout
                v-if="step === 8"
                :back="t('widget.quote.program.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 7"
            >
                <ProgramSelection
                    v-model="program"
                    :guest-types="selectedGuestTypes"
                    :guest-amount="guestAmount"
                    :guest-changed="guestAmountChanged"
                    @next="handleNextDecorationStep"
                />
            </Layout>

            <!-- Decoration Selection -->
            <Layout
                v-if="step === 9"
                :back="t('widget.quote.decoration.back')"
                :step="step"
                :total="totalSteps"
                @back="step = 8"
            >
                <Cards
                    v-model="decorationId"
                    :items="getFilteredProductsByCategory(4)"
                    :title="t('widget.quote.decoration.title')"
                    :skip-text="t('widget.buttons.skip')"
                    :show-price="venueData?.showProductPrices"
                    skip
                    @next="handleNextCategory"
                    @skip="handleNextCategory"
                />
            </Layout>

            <!-- Entertainment Selection -->
            <Layout
                v-if="step === 10"
                :back="t('widget.quote.entertainment.back')"
                :step="step"
                :total="totalSteps"
                @back="handleBackFromEntertainmentSelection"
            >
                <Cards
                    v-model="entertainmentId"
                    :items="getFilteredProductsByCategory(5)"
                    :title="t('widget.quote.entertainment.title')"
                    :skip-text="t('widget.buttons.skip')"
                    :show-price="venueData?.showProductPrices"
                    skip
                    @next="step = 11"
                    @skip="step = 11"
                />
            </Layout>

            <!-- Space Selection -->
            <Layout
                v-if="step === 11"
                :back="t('widget.quote.space.back')"
                :step="step"
                :total="totalSteps"
                @back="handleBackFromSpaceSelection"
            >
                <SpaceSelection
                    v-model="spaceIds"
                    :items="spacesForEventType"
                    @next="step = 12"
                />
            </Layout>

            <!-- Loading Screen -->
            <Layout
                v-if="step === 12"
                :step="step"
                :total="totalSteps"
            >
                <LoadingCard
                    :title="t('widget.loading.offerte-title')"
                    :subtitle="t('widget.loading.quote-subtitle', { event: eventName })"
                    background
                    @next="step = 13"
                />
            </Layout>

            <!-- Category Selection -->
            <Layout
                v-if="step === 13"
                :step="step"
                :total="totalSteps"
                no-header
            >
                <SmallCards
                    v-model="productCategoryId"
                    :items="productCategoryItems"
                    :title="t('widget.quote.category.title')"
                    :subtitle="t('widget.quote.category.subtitle')"
                    :skip-text="t('widget.quote.category.skip')"
                    no-header
                    skip
                    align-center
                    @next="step = 14"
                    @skip="step = 15"
                />
            </Layout>

            <!-- Product Shop -->
            <Layout
                v-if="step === 14"
                :step="step"
                :total="totalSteps"
                no-header
            >
                <Shop
                    v-model="productIds"
                    :products="productsForEventType"
                    :categories="categoriesWithProducts"
                    @next="step = 15"
                />
            </Layout>

            <!-- Final Form -->
            <Layout
                v-if="step === 15"
                :step="step"
                :total="totalSteps"
                :back="t('widget.quote.form.back')"
                no-progress
                @back="step = 14"
            >
                <Form
                    :venue="props.venue"
                    :locale="props.locale"
                    :event-date="selectedDate"
                    :event-type-id="eventTypeId"
                    :product-ids="productIds"
                    :space-ids="spaceIds"
                    :programme="program"
                    :timeframe-id="timeframeId"
                    :timeframes="timeframes"
                    :survey-response="surveyAppointment"
                    :survey-items="surveyAppointmentItems"
                    :guest-amount="guestAmount"
                    :booker-name="bookerName"
                    @next="step = 16"
                />
            </Layout>

            <!-- Congratulation step -->
            <Layout
                v-if="step === 16"
                :step="step"
                :total="totalSteps"
                no-header
            >
                <SubmitConfirmation
                    mode="quote"
                    @back-to-main="emit('backToMain')"
                />
            </Layout>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.quote {
    position: relative;
    block-size: 100%;
    inline-size: 100%;
}
</style>
